<template>
  <section v-if="isGetRequestPending" class="edit-tool">
    <p-loading />
  </section>
  <section v-else-if="item" class="edit-tool">
    <header>
      <h1>
        <span v-if="comparison">
          Comparing: <span class="tool-name">{{ item && item.name }}</span> tool
        </span>
        <span v-else>
          Edit <span class="tool-name">{{ item && item.name }}</span> tool
        </span>
      </h1>
      <div v-if="isViewingHistoricalVersion && !comparison" class="version-indicator">
        <p-md-icon name="eye" size="sm" />
        Viewing v{{ item.version }}
        <span class="current-label">(Current: v{{ originalVersion }})</span>
      </div>
    </header>
    <div class="content-wrapper">
      <p-compare-tool v-if="comparison" :comparison="comparison" @close="closeComparison" />
      <p-edit-tool-form
        v-else-if="item"
        :item="item"
        :is-request-pending="isUpdateRequestPending"
        :is-request-failed="isUpdateRequestFailed"
        @submit="submit"
        @cancel="$router.back()"
      />
      <p-tool-history
        :tool-name="originalToolName"
        :current-version="originalVersion"
        @version-activated="reloadTool"
        @version-deleted="reloadTool"
        @compare-version="onCompare"
        @preview-version="onPreview"
      />
    </div>
    <footer></footer>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { cloneDeep } from 'lodash';

import Loading from '@/components/common/Loading';
import EditForm from './_CreateOrEdit';
import ToolHistory from './ToolHistory';
import CompareTool from './CompareTool';
import MdIcon from '@/components/common/MdIcon';
import Button from '@/components/common/Button';
import 'jsondiffpatch/lib/formatters/styles/html.css';
import { toolDiffAsHtml } from './diff';

export default {
  components: {
    'p-loading': Loading,
    'p-edit-tool-form': EditForm,
    'p-tool-history': ToolHistory,
    'p-compare-tool': CompareTool,
    'p-md-icon': MdIcon
  },
  data() {
    return {
      item: null,
      comparison: null,
      originalToolName: null,
      originalVersion: null
    };
  },
  computed: {
    ...mapState({
      isGetRequestPending: s => s.nlp.getToolPending,
      isUpdateRequestPending: s => s.nlp.updateToolPending,
      isUpdateRequestFailed: s => s.nlp.updateToolFailed,
      toolHistory: s => s.nlp.toolHistory
    }),
    isViewingHistoricalVersion() {
      return this.item && this.originalVersion && this.item.version !== this.originalVersion;
    }
  },
  watch: {
    '$route.query.compareVersion': async function(newVersion) {
      if (newVersion) {
        await this.loadComparison(parseInt(newVersion));
      } else {
        this.comparison = null;
      }
    }
  },
  async created() {
    this.item = await this.$store.dispatch('nlp/getTool', this.$route.params.id);
    this.originalToolName = this.item.name;
    this.originalVersion = this.item.version;

    // Check if we should load comparison view from URL query
    if (this.$route.query.compareVersion) {
      await this.loadComparison(parseInt(this.$route.query.compareVersion));
    }
  },
  methods: {
    async submit(props) {
      const confirm = await this.$confirm({
        title: `Save new version?`,
        message: `Are you sure you want to save a new version?`,
        confirm: 'Save',
        userResponseLabel: 'Comment'
      });

      if (!confirm) {
        return;
      }

      await this.$store.dispatch('nlp/updateTool', {
        ...props,
        message: confirm.userResponse
      });

      if (!this.isUpdateRequestFailed) {
        this.$toast.success({
          title: 'Tool updated',
          message: `Tool has been updated successfully.`
        });
        // Refresh tool history after update
        await this.$store.dispatch('nlp/getToolHistory', this.item.name);
        this.$router.push({ path: `/tools` });
      }
    },
    async reloadTool() {
      this.item = await this.$store.dispatch('nlp/getTool', this.$route.params.id);
      this.originalVersion = this.item.version;
    },
    async onCompare(selectedVersion) {
      // Navigate to comparison view using query parameter
      await this.$router.push({
        path: this.$route.path,
        query: { compareVersion: selectedVersion.version }
      });
    },
    async onPreview(selectedVersion) {
      // Close comparison if it's open
      if (this.comparison) {
        await this.closeComparison();
      }
      this.item = cloneDeep(selectedVersion);
    },
    closeComparison() {
      this.$router.push({ path: this.$route.path });
    },
    async loadComparison(version) {
      const selectedVersion = this.toolHistory.find(v => v.version === version);

      if (!selectedVersion) {
        return;
      }

      const currentTool = cloneDeep(this.item);
      const selectedTool = cloneDeep(selectedVersion);

      const version1 = `Current Version ${currentTool.version}`;
      const version2 = `Version ${selectedVersion.version}`;

      this.comparison = {
        version1,
        version2,
        html: await toolDiffAsHtml(selectedTool, currentTool)
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.edit-tool {
  display: grid;
  padding: 0;
  grid-template-columns: 1fr;
  grid-template-rows: max-content minmax(0, 1fr);
  grid-gap: 0;
  width: 100%;
  height: 100%;

  .tool-name {
    color: var(--theme-primary);
  }

  > header {
    padding: 0.5rem 1rem 0.5rem 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;

    h1 {
      color: var(--theme-on-surface);
      font-size: 1.5rem;
      font-weight: 700;
      margin-right: 0.25rem;
    }

    .version-indicator {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: var(--theme-on-primary);
      font-size: 0.9rem;
      font-weight: 500;
      padding: 0.4rem 0.75rem;
      background: var(--theme-primary);
      border-radius: 4px;

      .current-label {
        color: var(--theme-on-primary);
        opacity: 0.85;
        font-size: 0.85rem;
        font-weight: 400;
        margin-left: 0.25rem;
      }
    }

    .action-wrapper {
      display: flex;
      justify-content: flex-end;
      align-items: center;
    }
  }

  .content-wrapper {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 5px;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }
}
</style>
